import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";
import { Button } from "./ui/button.js";
import { Loader2 } from "lucide-react";
import { validarRut, normalizeRut, formatRut } from "../../lib/rut.ts";

export interface SiiCredentials {
  rutSii: string;
  claveSii: string;
  claveCertificado?: string;
}

const CAUSAS_ANULACION = [
  { value: 1, label: "No se efectuó el pago", desc: "No se efectuó el pago de los servicios por parte del receptor." },
  { value: 2, label: "No se prestó el servicio", desc: "No se efectuó la prestación de servicios." },
  { value: 3, label: "Error en la digitación", desc: "Error en la digitación de los datos de la boleta." },
] as const;

export default function ClaveSiiModal({
  open,
  onClose,
  onSubmit,
  onSubmitWithCausa,
  requiresCertificado,
  anulacionMode = false,
  onMarcarAnuladoManual,
  documentCount = 1,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (creds: SiiCredentials) => void;
  onSubmitWithCausa?: (creds: SiiCredentials, causa: number) => void;
  requiresCertificado?: boolean;
  anulacionMode?: boolean;
  onMarcarAnuladoManual?: () => void;
  documentCount?: number;
}) {
  const [rutSii, setRutSii] = useState("");
  const [claveSii, setClaveSii] = useState("");
  const [claveCertificado, setClaveCertificado] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [causa, setCausa] = useState<number>(1);
  const [rutError, setRutError] = useState<string | null>(null);
  const [claveError, setClaveError] = useState<string | null>(null);
  const [certError, setCertError] = useState<string | null>(null);

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9kK.-]/g, "");
    setRutSii(val);
    if (rutError) setRutError(null);
  };

  const handleRutBlur = () => {
    if (!rutSii.trim()) return;
    const formatted = formatRut(rutSii.trim());
    setRutSii(formatted);
    if (!validarRut(formatted)) {
      setRutError("El RUT ingresado no es válido (verifica el dígito verificador)");
    } else {
      setRutError(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!rutSii.trim()) {
      setRutError("Debes ingresar el RUT autorizado");
      return;
    }
    const formatted = formatRut(rutSii.trim());
    setRutSii(formatted);

    if (!validarRut(formatted)) {
      setRutError("El RUT ingresado no es válido (verifica el dígito verificador)");
      toast.error("El RUT ingresado no es válido");
      return;
    }
    if (!claveSii.trim()) {
      setClaveError("Debes ingresar la clave SII");
      return;
    }
    if (requiresCertificado && !claveCertificado.trim()) {
      setCertError("Debes ingresar la clave del certificado digital");
      return;
    }

    setSubmitting(true);
    const creds = { rutSii: normalizeRut(formatted), claveSii: claveSii.trim(), claveCertificado: claveCertificado.trim() || undefined };
    if (anulacionMode && onSubmitWithCausa) {
      onSubmitWithCausa(creds, causa);
    } else {
      onSubmit(creds);
    }
    setClaveSii("");
    setClaveCertificado("");
    setRutError(null);
    setClaveError(null);
    setCertError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    setClaveSii("");
    setClaveCertificado("");
    setRutError(null);
    setClaveError(null);
    setCertError(null);
    onClose();
  };

  const isMultiple = documentCount > 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {anulacionMode
              ? (requiresCertificado
                  ? (isMultiple ? `Emitir Notas de Crédito para Anular (${documentCount} documentos)` : "Emitir Nota de Crédito para Anular")
                  : (isMultiple ? `Anular ${documentCount} boletas de honorarios` : "Anular boleta de honorarios"))
              : "Credenciales SII"}
          </DialogTitle>
          <DialogDescription>
            {anulacionMode
              ? (requiresCertificado
                  ? (isMultiple
                      ? `Ingresa tus credenciales del portal SII y la clave de tu certificado digital para emitir las ${documentCount} Notas de Crédito (DTE 61). Esta acción es legalmente vinculante ante el SII.`
                      : "Ingresa tus credenciales del portal SII y la clave de tu certificado digital para emitir la Nota de Crédito (DTE 61). Esta acción es legalmente vinculante ante el SII.")
                  : (isMultiple
                      ? `Selecciona el motivo de anulación e ingresa tus credenciales SII para anular las ${documentCount} boletas seleccionadas. Esta acción es irreversible.`
                      : "Selecciona el motivo de anulación e ingresa tus credenciales SII. Esta acción es irreversible."))
              : "Ingresa tu RUT y clave SII para emitir documentos. Estas credenciales no se guardan y se solicitan una vez por lote de emisión."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {anulacionMode && !requiresCertificado && (
            <div className="space-y-3">
              <Label>Motivo de anulación</Label>
              {CAUSAS_ANULACION.map((c) => (
                <label
                  key={c.value}
                  className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-colors ${causa === c.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"}`}
                >
                  <input
                    type="radio"
                    name="causa"
                    value={c.value}
                    checked={causa === c.value}
                    onChange={() => setCausa(c.value)}
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <p className="text-sm font-medium">{c.label}</p>
                    <p className="text-xs text-muted-foreground">{c.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="rutSii">RUT usuario autorizado SII</Label>
            <Input
              id="rutSii"
              type="text"
              placeholder="12.345.678-9"
              value={rutSii}
              onChange={handleRutChange}
              onBlur={handleRutBlur}
              className={`font-mono transition-colors ${rutError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              required
              autoFocus
            />
            {rutError && (
              <p className="text-xs text-destructive font-medium mt-1">{rutError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="claveSii">Clave SII</Label>
            <Input
              id="claveSii"
              type="password"
              placeholder="••••••••"
              value={claveSii}
              onChange={(e) => {
                setClaveSii(e.target.value);
                if (claveError) setClaveError(null);
              }}
              className={`transition-colors ${claveError ? "border-destructive focus-visible:ring-destructive" : ""}`}
              required
            />
            {claveError && (
              <p className="text-xs text-destructive font-medium mt-1">{claveError}</p>
            )}
          </div>
          {requiresCertificado && (
            <div className="space-y-1.5">
              <Label htmlFor="claveCertificado">Clave del certificado digital SII</Label>
              <Input
                id="claveCertificado"
                type="password"
                placeholder="••••••••"
                value={claveCertificado}
                onChange={(e) => {
                  setClaveCertificado(e.target.value);
                  if (certError) setCertError(null);
                }}
                className={`transition-colors ${certError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                required={requiresCertificado}
              />
              {certError ? (
                <p className="text-xs text-destructive font-medium mt-1">{certError}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Necesaria para firmar facturas electrónicas y notas de crédito.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting || !rutSii.trim() || !claveSii.trim() || (requiresCertificado && !claveCertificado.trim())}
              variant={anulacionMode ? "destructive" : "default"}
              className={anulacionMode ? "bg-red-600 hover:bg-red-700 font-bold shadow-md text-white border-0" : ""}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {anulacionMode ? (requiresCertificado ? "Emitiendo Nota de Crédito..." : "Anulando...") : "Procesando..."}
                </>
              ) : anulacionMode ? (
                requiresCertificado
                  ? (isMultiple ? `⚠️ Confirmar y Emitir ${documentCount} Notas de Crédito` : "⚠️ Confirmar y Emitir Nota de Crédito")
                  : (isMultiple ? `⚠️ Confirmar Anulación de ${documentCount} boletas` : "⚠️ Confirmar Anulación")
              ) : (
                "Confirmar y emitir"
              )}
            </Button>
          </DialogFooter>
          {anulacionMode && onMarcarAnuladoManual && (
            <div className="pt-3 mt-3 border-t text-center space-y-1.5 bg-muted/30 -mx-6 -mb-6 p-4 rounded-b-lg">
              <p className="text-xs text-muted-foreground">
                ¿Ya emitiste la Nota de Crédito o anulaste directamente en el portal del SII?
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  handleClose();
                  onMarcarAnuladoManual();
                }}
                className="text-xs text-muted-foreground hover:text-foreground h-8 font-normal underline-offset-4 hover:underline"
              >
                Marcar como anulado aquí (sin emitir Nota de Crédito)
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
